import { CSSProperties, ForwardedRef, forwardRef } from 'react';
import styles from './SimpleFile.module.scss';
import DownloadButton from '../shared/DownloadButton';
import { ServiceDownload } from '../../services/service-download';
import { FileScope } from '../../util/appEnum';
import { ScopeOptions } from '../../models/global.model';
import _ from 'lodash';
import { ReactComponent as FileIcon } from '../../assets/svg/file.svg';
import { useTranslation } from 'react-i18next';
import Spinner from '../shared/Spinner/Spinner';
import MiddleEllipsis from 'react-middle-ellipsis';

type Props = {
    fileName: string,
    disabled?: boolean,
    file?: string,
    url?: string,
    style?: CSSProperties,
    scope?: FileScope,
    scopeOptions?: ScopeOptions,
    comment?: string,
    submittedDate?: Date,
    validUntilDate?: Date,
    blob?: Blob,
    className?: string,
    noFile?: boolean,
}

const SimpleFile = forwardRef(function SimpleFile({
    style,
    fileName = '',
    disabled,
    file,
    url,
    scope,
    scopeOptions,
    comment,
    submittedDate,
    validUntilDate,
    blob,
    className,
    noFile,
}: Props, ref: ForwardedRef<HTMLDivElement>) {

    const getFilename = (file: string) => {
        return file?.slice(((file.lastIndexOf('/') - 1) >>> 0) + 2) || file;
    }
    const [t] = useTranslation('supplierDatabase', { keyPrefix: 'supplierDetailPanel' });

    const activeLabel = t('liabilityInsurance.activeLabel', 'Aktiv')
    const inactiveLabel = t('liabilityInsurance.inactiveLabel', 'Utgången')
    let active = false;
    let end;
    const pickedDate = validUntilDate;
    if (pickedDate) {
        end = new Date(pickedDate);
        end.setHours(23, 59, 59, 999);
        active = new Date() < end;
    }
    return (
        <DownloadButton
            disabled={disabled}
            url={url || (file && !_.isEmpty(file) && scope && !_.isEmpty(scopeOptions) && ServiceDownload.getFileDownloadURL(file, scope, scopeOptions)) || ''}
            blob={blob}
            fileName={fileName}
            render={({ downloading, progress, onClick }) => {
                const hasFile = file && fileName;
                const showFullDetails = !noFile;

                return (
                    <div
                        ref={ref}
                        className={`${className} ${styles.container} ${disabled ? styles.disabled : ''} ${!file ? styles['container--no-file'] : ''}`}
                        style={style}
                        onClick={onClick}
                        key={fileName + (validUntilDate || submittedDate)}
                    >
                        <div className={styles['container__file-holder']}>
                            {hasFile && showFullDetails && (
                                <div className={styles['container__file-icon']}>
                                    <FileIcon />
                                </div>
                            )}
                            <div className={styles['container__name-comment']}>
                                <div className={`${styles['container__name']} ${!hasFile ? styles['container__name--no-file'] : ''}`}>
                                    {hasFile ? <MiddleEllipsis>
                                        <span className="ellipseMe">{getFilename(fileName)}</span>
                                    </MiddleEllipsis> : t('no.file.found', 'Ingen fil hittades')}
                                </div>
                                {hasFile && showFullDetails && comment && (
                                    <div className={styles['container__comment']}>
                                        {comment}
                                    </div>
                                )}
                            </div>
                        </div>

                        {showFullDetails && downloading && (
                            <Spinner isHidden={!downloading} size='xxs' progress={progress} />
                        )}

                        {showFullDetails && pickedDate && hasFile && (
                            <div className={`${styles['container__status']} ${!active ? styles['container__status--inactive'] : ''}`}>
                                <span>{active ? activeLabel : inactiveLabel}</span>
                            </div>
                        )}
                    </div>
                );
            }}
        />
    )
})

export default SimpleFile;
